/**
 * stl-product-more.js - slice 4b: everything below the fold on a product page.
 *
 *  1. Size guide pop-over: tidies the Shopify page content it shows, and switches cm / inches.
 *  2. Story player: plays a product's clips full screen, muted and inline, with a pause button and
 *     a tap on the frame to stop it, a sound button, swipe and arrows between clips, the clip
 *     released again when it closes, and the clip held on its poster under reduce motion.
 *  3. Reviews: our "Write a review" control opens Judge.me's own form and stays hidden until
 *     Judge.me's widget is really on the page; the store-wide rating in the empty state is read
 *     off Judge.me's own container rather than left to two theme settings; and a review title
 *     that only repeats its own author's name is hidden.
 *
 * Loaded with <script type="module" src="..."> from the sections that need it. A module runs once
 * per URL however many tags ask for it, so the sections need not co-ordinate.
 */
import { onReady, delegate, openOverlay, closeOverlay } from '@stl/core';

const qs = (sel, root = document) => root.querySelector(sel);
const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/* ------------------------------------------------------------------ 1. size guide ------- */

/**
 * The guide is the body of a Shopify page, typed in the admin editor over three years. It carries
 * inline column widths in fractional pixels, stray <meta charset> tags and TinyMCE classes, which
 * overflow a 390px phone. Strip them: an inline width cannot be beaten by a stylesheet.
 */
/* A guide page sometimes carries its own AUS / US / EURO conversion rows. The modal and the size
   guide page show ONE conversion table (the site-wide one under the measurements), so a table
   whose rows are only size-conversion labels and holds no measurement is dropped from the render
   (24 Sep: two tables saying nearly the same thing, sometimes disagreeing, confused Chris). */
const CONVERSION_ROW = /^(aus?|au\s*\/|aus\s*\/|us|usa|uk|euro?|eu|size)\b/i;
const MEASUREMENT = /\b(cm|inch|inches|bust|waist|hip|hips|length|sleeve|chest|shoulder|rise|inseam)\b|\d+\s*(cm|in)\b/i;
function dropConversionTables(box) {
  qsa('table', box).forEach((table) => {
    const rows = qsa('tr', table);
    if (!rows.length) return;
    const text = table.textContent || '';
    if (MEASUREMENT.test(text)) return;
    const labelled = rows.filter((row) => CONVERSION_ROW.test((row.cells[0] && row.cells[0].textContent || '').trim()));
    if (labelled.length >= Math.max(2, rows.length - 1)) table.remove();
  });
}

function tidyGuide(box) {
  qsa('meta', box).forEach((el) => el.remove());
  dropConversionTables(box);
  qsa('*', box).forEach((el) => {
    el.removeAttribute('style');
    el.removeAttribute('class');
    el.removeAttribute('id');
    el.removeAttribute('width');
    el.removeAttribute('height');
    el.removeAttribute('align');
    el.removeAttribute('bgcolor');
  });
}

function sizeGuideUnit(btn) {
  const box = btn.closest('.stl-modal-box') || document;
  const unit = btn.dataset.stlSgUnit;
  qsa('[data-stl-size-guide]', box).forEach((el) => {
    el.hidden = el.dataset.stlSizeGuide !== unit;
  });
  qsa('[data-stl-sg-unit]', box).forEach((el) => {
    const on = el === btn;
    el.classList.toggle('stl-is-on', on);
    el.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

/* ------------------------------------------------------------------ 2. story player ----- */

let clips = [];
let at = 0;

function player() {
  return document.getElementById('stl-player');
}

function clipsFor(btn) {
  let el = btn;
  let tag = null;
  while (el && !tag) {
    tag = el.querySelector ? el.querySelector('[data-stl-stories]') : null;
    el = el.parentElement;
  }
  if (!tag) tag = qs('[data-stl-stories]');
  if (tag) {
    try {
      const list = JSON.parse(tag.textContent);
      if (Array.isArray(list) && list.length) return list;
    } catch (err) {
      console.warn('stl-product-more: a [data-stl-stories] playlist is not valid JSON', err);
    }
  }
  const withSrc = qsa('[data-stl-story][data-stl-video]');
  if (withSrc.length) {
    return withSrc.map((b) => ({
      src: b.dataset.stlVideo,
      poster: b.dataset.stlPoster,
      label: b.dataset.stlLabel,
    }));
  }
  if (btn.dataset.stlVideo) {
    return [{ src: btn.dataset.stlVideo, poster: btn.dataset.stlPoster, label: btn.dataset.stlLabel }];
  }
  return [];
}

/**
 * Read live, never cached: a check may stub window.matchMedia to prove the stopped branch, and a
 * reader can change the setting while the page is open.
 */
function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

/** The pause button says what it will DO, and its icon says what is happening. */
function syncPlayButton() {
  const box = player();
  const btn = box && qs('[data-stl-player-pause]', box);
  const video = box && qs('[data-stl-player-video]', box);
  if (!btn || !video) return;
  const playing = !video.paused;
  const pauseIco = qs('[data-stl-pause-icon]', btn);
  const playIco = qs('[data-stl-play-icon]', btn);
  /* toggleAttribute, never `.hidden`: the two icons are <svg>, and `hidden` is an HTMLElement
     property. On an SVGElement `el.hidden = false` sets a plain JavaScript property and leaves
     the hidden ATTRIBUTE exactly where it was, so the play glyph never appeared (measured
     22 Sep 2026: aria-label read Play while the pause bars were still on screen). */
  if (pauseIco) pauseIco.toggleAttribute('hidden', !playing);
  if (playIco) playIco.toggleAttribute('hidden', playing);
  btn.setAttribute(
    'aria-label',
    playing ? btn.dataset.stlPauseLabel || 'Pause' : btn.dataset.stlPlayLabel || 'Play'
  );
}

function togglePlay() {
  const box = player();
  const video = box && qs('[data-stl-player-video]', box);
  if (!video) return;
  if (video.paused) {
    const go = video.play();
    if (go && go.catch) go.catch(() => {});
  } else {
    video.pause();
  }
  syncPlayButton();
}

function show(i) {
  const box = player();
  if (!box || !clips.length) return;
  at = (i + clips.length) % clips.length;
  const clip = clips[at] || {};
  const video = qs('[data-stl-player-video]', box);
  const title = qs('[data-stl-player-title]', box);
  const sound = qs('[data-stl-player-sound]', box);
  if (title) title.textContent = clip.label || '';
  if (video) {
    video.muted = true;
    if (clip.poster) video.setAttribute('poster', clip.poster);
    else video.removeAttribute('poster');
    video.setAttribute('src', clip.src || '');
    video.load();
    /* Reduce motion means the clip waits on its poster until the reader presses play. */
    if (reducedMotion()) {
      video.pause();
    } else {
      const go = video.play();
      if (go && go.catch) go.catch(() => {});
    }
    syncPlayButton();
  }
  if (sound) {
    sound.setAttribute('aria-pressed', 'false');
    sound.textContent = sound.dataset.stlSoundOff || 'Tap for sound';
  }
  const many = clips.length > 1;
  const prev = qs('[data-stl-player-prev]', box);
  const next = qs('[data-stl-player-next]', box);
  if (prev) prev.hidden = !many;
  if (next) next.hidden = !many;
}

function stop() {
  const box = player();
  if (!box) return;
  const video = qs('[data-stl-player-video]', box);
  if (!video) return;
  video.pause();
  video.removeAttribute('src');
  video.removeAttribute('poster');
  video.load();
}

function toggleSound() {
  const box = player();
  const video = box && qs('[data-stl-player-video]', box);
  const sound = box && qs('[data-stl-player-sound]', box);
  if (!video) return;
  video.muted = !video.muted;
  if (!video.muted) {
    const go = video.play();
    if (go && go.catch) go.catch(() => {});
  }
  if (sound) {
    sound.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
    sound.textContent = video.muted
      ? sound.dataset.stlSoundOff || 'Tap for sound'
      : sound.dataset.stlSoundOn || 'Sound on';
  }
}

function isOpen() {
  const box = player();
  return !!box && box.classList.contains('stl-is-on');
}

/* ------------------------------------------------------------------ 3. reviews ---------- */

const JDGM_WRITE = '.stl-section--product-reviews .jdgm-write-rev-link';

function revealWriteButton() {
  const link = qs(JDGM_WRITE);
  if (!link) return false;
  qsa('[data-stl-jdgm-write]').forEach((btn) => {
    btn.hidden = false;
  });
  return true;
}

/**
 * The empty state ("no reviews on this piece yet") quotes the store-wide rating, which Liquid can
 * only take from two theme settings. Those settings go stale the moment a review lands, and the
 * Judge.me widget one box lower prints the live figure, so the page contradicted itself (4.9 from
 * 82 against 4.99 from 99, measured 22 Sep 2026). Judge.me carries the live pair on its own
 * container, so read it from there and let the settings be the pre-JS fallback only.
 *
 * One decimal, because that is how every other rating on the page is written, and rounded DOWN:
 * 4.99 is shown as 4.9, never as a flat 5.0. A rating we print about ourselves understates or it
 * is not worth printing.
 */
function applyStoreRating() {
  const line = qs('[data-stl-store-line]');
  if (!line) return false;
  const widget = qs('#judgeme_product_reviews');
  if (!widget) return false;
  const avg = parseFloat(widget.getAttribute('data-shop-average-rating'));
  const count = parseInt(widget.getAttribute('data-shop-review-count'), 10);
  if (!isFinite(avg) || avg <= 0 || !isFinite(count) || count <= 0) return false;
  const shown = Math.floor(avg * 10) / 10;
  const text = shown.toFixed(1) + ' from ' + count + (count === 1 ? ' review' : ' reviews');
  if (line.textContent.trim() !== text) line.textContent = text;
  const stars = qs('[data-stl-store-stars]');
  if (stars) stars.style.width = Math.round(shown * 20) + '%';
  return true;
}

/**
 * Judge.me keeps the reviewer's NAME in the title field of some reviews, so a card printed
 * "Bailey" as an 18px serif heading and "Bailey" again in the author line underneath. Hide a
 * title that only repeats its own author. Nothing a customer actually wrote is ever hidden: the
 * body, the stars, the name, the date and the verified badge are all untouched, and a title that
 * says anything else stays exactly where it is.
 */
function tidy(s) {
  return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function dedupeReviewTitles() {
  qsa('.stl-section--product-reviews .jdgm-rev').forEach((card) => {
    const title = qs('.jdgm-rev__title', card);
    const author = qs('.jdgm-rev__author', card);
    if (!title || !author) return;
    const t = tidy(title.textContent);
    const a = tidy(author.textContent);
    /* equal, or the title is the author's first name or names */
    const dupe = !!t && (t === a || a.indexOf(t + ' ') === 0);
    if (title.hidden !== dupe) title.hidden = dupe;
  });
}

/** Everything the reviews section wants doing once Judge.me's script has drawn itself. */
function syncReviews() {
  const write = revealWriteButton();
  const rating = applyStoreRating();
  dedupeReviewTitles();
  /* the empty state has no cards, so "done" is whichever of the two this page actually has */
  return qs('[data-stl-store-line]') ? rating : write;
}

/* ------------------------------------------------------------------ wiring -------------- */

onReady(() => {
  qsa('[data-stl-size-guide]').forEach(tidyGuide);
  /* the US market opens on inches: the modal marks the in button as the default */
  const dfltUnit = qsa('[data-stl-sg-unit][data-stl-sg-default="in"]')[0];
  if (dfltUnit) sizeGuideUnit(dfltUnit);

  const host = qs('.stl-section--product-reviews');
  if (host) {
    syncReviews();
    /* Judge.me draws its widget, and every later page of reviews, long after DOM-ready, so the
       observer stays on for twenty seconds rather than stopping at the first success. It watches
       the two store-wide attributes as well, because their script writes them onto a container
       that is already in the DOM. Nothing here writes what it is watching, so it cannot loop. */
    if (window.MutationObserver) {
      const watch = new MutationObserver(() => {
        syncReviews();
      });
      watch.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-shop-average-rating', 'data-shop-review-count'],
      });
      setTimeout(() => watch.disconnect(), 20000);
    }
  }

  /* play and pause do not bubble, so the button is kept honest from the element itself: the clip
     can also stop on its own (a network stall, the reader's own media keys). */
  const box = player();
  const video = box && qs('[data-stl-player-video]', box);
  if (video) {
    video.addEventListener('play', syncPlayButton);
    video.addEventListener('pause', syncPlayButton);
    syncPlayButton();
  }
});

delegate(document, 'click', '[data-stl-sg-unit]', (event, el) => {
  event.preventDefault();
  sizeGuideUnit(el);
});

/**
 * The visible review form, in whichever of Judge.me's three shapes it arrives.
 *
 * Judge.me's own link opens a FULL-SCREEN MODAL (.jdgm-write-review-modal, appended to <body>,
 * so it is outside our section) and leaves the inline .jdgm-form-wrapper at display:none. Looking
 * for the inline form alone therefore missed every real open and fired the fallback every time.
 *
 * Measured on the box, never on the display value: the inline form reports display:block at 0x0
 * whenever its hidden wrapper is above it.
 */
function jdgmForm() {
  const sel = [
    '.jdgm-write-review-modal',
    '.stl-section--product-reviews .jdgm-form-wrapper',
    '.stl-section--product-reviews .jdgm-form',
  ].join(', ');
  return qsa(sel).find((el) => {
    const box = el.getBoundingClientRect();
    return box.height > 40 && box.width > 0;
  });
}

/* Our control is a proxy for Judge.me's own link, so the form that opens is theirs. If their
   script has not bound its click yet, nothing would happen, so after a moment we show their own
   control instead of leaving a button that does nothing. */
delegate(document, 'click', '[data-stl-jdgm-write]', (event) => {
  event.preventDefault();
  const link = qs(JDGM_WRITE);
  if (!link) return;
  link.click();
  setTimeout(() => {
    const form = jdgmForm();
    if (form) {
      /* The modal is fixed and already holds focus: scrolling the page behind it does nothing
         useful and moves the reader's place. Only the inline form needs bringing into view. */
      if (!form.classList.contains('jdgm-write-review-modal')) {
        form.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return;
    }
    const section = qs('.stl-section--product-reviews');
    if (section) section.classList.add('stl-pm-rev-fallback');
    link.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (link.focus) link.focus();
  }, 700);
});

/* The bubble's own click must set the clip BEFORE @stl/core opens the overlay, so this listens in
   the capture phase. A bubble with no data-stl-open is opened here instead. */
document.addEventListener(
  'click',
  (event) => {
    const btn = event.target.closest && event.target.closest('[data-stl-story], [data-stl-video]');
    if (!btn || !player()) return;
    clips = clipsFor(btn);
    if (!clips.length) return;
    const i = parseInt(btn.dataset.stlStory, 10);
    show(Number.isFinite(i) ? i : 0);
    if (!btn.hasAttribute('data-stl-open')) {
      event.preventDefault();
      openOverlay('stl-player', { returnTo: btn });
    }
  },
  true
);

delegate(document, 'click', '[data-stl-player-next]', () => show(at + 1));
delegate(document, 'click', '[data-stl-player-prev]', () => show(at - 1));
delegate(document, 'click', '[data-stl-player-sound]', toggleSound);
delegate(document, 'click', '[data-stl-player-pause]', togglePlay);
/* A tap on the frame stops and starts the clip. It used to turn the sound on, which left the
   moving picture with no way to stop it but closing the player (WCAG 2.2.2). The sound button
   is still the way to hear it. */
delegate(document, 'click', '[data-stl-player-video]', togglePlay);

document.addEventListener('click', (event) => {
  if (isOpen() && event.target === player()) closeOverlay('stl-player');
});

document.addEventListener('keydown', (event) => {
  if (!isOpen() || clips.length < 2) return;
  if (event.key === 'ArrowRight') show(at + 1);
  if (event.key === 'ArrowLeft') show(at - 1);
});

let swipeFrom = null;
document.addEventListener(
  'touchstart',
  (event) => {
    if (!isOpen()) return;
    swipeFrom = event.changedTouches[0].clientX;
  },
  { passive: true }
);

document.addEventListener(
  'touchend',
  (event) => {
    if (!isOpen() || swipeFrom === null) return;
    const dx = event.changedTouches[0].clientX - swipeFrom;
    swipeFrom = null;
    if (clips.length > 1 && Math.abs(dx) > 40) show(dx < 0 ? at + 1 : at - 1);
  },
  { passive: true }
);

document.addEventListener('stl:overlay:change', (event) => {
  const d = event.detail || {};
  if (d.id === 'stl-player' && d.open === false) stop();
});
